/**
 * React Query hooks for LPA Questions.
 *
 * Questions are factory-scoped (tenant_id + factory_id).
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/core/lib/queryKeys';
import { useFactory } from '@/core/hooks/useFactory';
import { useTenant } from '@/core/hooks/useTenant';
import { useToast } from '@/shared/ui/use-toast';
import { useLanguage } from '@/core/LanguageContext';
import {
  fetchQuestions,
  createQuestion,
  updateQuestion,
  deleteQuestion,
} from '../api/questions';
import type { CreateQuestionInput, UpdateQuestionInput } from '../types';

// ---------------------------------------------------------------------------
// Query
// ---------------------------------------------------------------------------

export function useQuestions() {
  const { selectedFactoryId } = useFactory();

  return useQuery({
    queryKey: queryKeys.modLpaQuestions(selectedFactoryId),
    queryFn: () => fetchQuestions(selectedFactoryId!),
    enabled: !!selectedFactoryId,
  });
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

export function useCreateQuestion() {
  const queryClient = useQueryClient();
  const { selectedFactoryId } = useFactory();
  const { selectedTenantId } = useTenant();
  const { toast } = useToast();
  const { t } = useLanguage();

  return useMutation({
    mutationFn: (
      input: Omit<CreateQuestionInput, 'tenant_id' | 'factory_id'>,
    ) =>
      createQuestion({
        ...input,
        tenant_id: selectedTenantId!,
        factory_id: selectedFactoryId!,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.modLpaQuestions(selectedFactoryId),
      });
      toast({
        title: t('mod_lpa.questions.createSuccess', 'Question created'),
      });
    },
    onError: (error: Error) => {
      toast({
        title: t('mod_lpa.questions.createError', 'Failed to create question'),
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useUpdateQuestion() {
  const queryClient = useQueryClient();
  const { selectedFactoryId } = useFactory();
  const { toast } = useToast();
  const { t } = useLanguage();

  return useMutation({
    mutationFn: ({
      questionId,
      input,
    }: {
      questionId: string;
      input: UpdateQuestionInput;
    }) => updateQuestion(questionId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.modLpaQuestions(selectedFactoryId),
      });
      toast({
        title: t('mod_lpa.questions.updateSuccess', 'Question updated'),
      });
    },
    onError: (error: Error) => {
      toast({
        title: t('mod_lpa.questions.updateError', 'Failed to update question'),
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useDeleteQuestion() {
  const queryClient = useQueryClient();
  const { selectedFactoryId } = useFactory();
  const { toast } = useToast();
  const { t } = useLanguage();

  return useMutation({
    mutationFn: (questionId: string) => deleteQuestion(questionId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.modLpaQuestions(selectedFactoryId),
      });
      toast({
        title: t('mod_lpa.questions.deleteSuccess', 'Question deleted'),
      });
    },
    onError: (error: Error) => {
      toast({
        title: t('mod_lpa.questions.deleteError', 'Failed to delete question'),
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}
